import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { Button } from "../../components/ui/Button";
import { cn } from "../../components/ui/cn";
import { Panel } from "../../components/ui/Panel";

export function HolidaySettings({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [date, setDate] = useState("");
  const [name, setName] = useState("");

  const hours = useQuery({
    queryKey: queryKeys.businessHours,
    queryFn: api.businessHours,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.businessHours });
    // Every countdown is measured against this calendar.
    void queryClient.invalidateQueries({ queryKey: queryKeys.ticketsRoot });
  };

  const create = useMutation({
    mutationFn: () => api.createHoliday({ date, name }),
    onSuccess: () => {
      setDate("");
      setName("");
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteHoliday(id),
    onSuccess: invalidate,
  });

  const week = hours.data?.week ?? [];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <Panel
      collapsible
      title="Business hours and bank holidays"
    >
      <div className="mb-4 flex flex-wrap gap-2">
        {week.map((day, index) => (
          <div
            key={index}
            className={cn(
              "rounded-md border border-line px-2.5 py-1.5 text-xs",
              day ? "text-content" : "text-faint",
            )}
          >
            <div className="font-medium">{dayNames[index]}</div>
            <div className="tabular-nums">
              {day
                ? `${formatMinuteOfDay(day.start)}–${formatMinuteOfDay(day.end)}`
                : "Closed"}
            </div>
          </div>
        ))}
      </div>

      {isAdmin ? (
        <form
          className="mb-3 flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (date && name.trim()) create.mutate();
          }}
        >
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className={cn(
              "h-9 rounded-md border border-line bg-surface px-2.5 text-sm text-content",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
            )}
          />
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Holiday name"
            className={cn(
              "h-9 flex-1 rounded-md border border-line bg-surface px-2.5 text-sm text-content",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
            )}
          />
          <Button type="submit" disabled={!date || !name.trim()}>
            Add
          </Button>
        </form>
      ) : null}

      <ul className="divide-y divide-line">
        {(hours.data?.holidays ?? []).map((holiday) => (
          <li
            key={holiday.id}
            className="flex items-center gap-3 py-1.5 text-sm"
          >
            <span className="w-28 shrink-0 tabular-nums text-muted">
              {holiday.date}
            </span>
            <span className="min-w-0 flex-1 truncate text-content">
              {holiday.name}
            </span>
            {isAdmin ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => remove.mutate(holiday.id)}
              >
                Remove
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function formatMinuteOfDay(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}
